import cn from 'classnames'
import { PartnerTypeButtons } from 'legacy/lut/components/widgets/public/partner360/PartnerTypeButtons'
import { useActualsData } from 'legacy/lut/features/partner360/configuration/hooks/useActualsData'
import { useCallback, useEffect, useMemo, useRef } from 'react'

import { FormNumberInput, useFormField, useGetFormDataField } from 'cng-common/features/form'
import { Translation } from 'cng-common/features/translations'
import { Nullable } from 'cng-common/models/utils'
import { RowButtons } from 'partner360/components/rowButtons/RowButtons'
import { ActualsValueTypes } from 'partner360/models/actualValueTypes'
import { P360Currency } from 'partner360/models/P360Currency'

import {
  calculateMdfEstimatedSales,
  IMdfActivityOutcomeForm,
  TMdfActivityOutcomeFieldChange,
} from '../../lib'

import { IMdfOutcomeMetricField, MDF_OUTCOME_METRIC_FIELDS } from './config'
import { MdfOutcomeItemsEditor } from './MdfOutcomeItemsEditor'

import styles from './MdfActivityOutcomeFields.module.scss'

export interface IMdfActivityOutcomeFieldsProps {
  formKey: string
  initialData: IMdfActivityOutcomeForm
  className?: string
  labelClassName?: string
  isDisabled?: boolean
  autoFocusOutcome?: boolean
  onChange?: TMdfActivityOutcomeFieldChange
}

export const MdfActivityOutcomeFields = ({
  formKey,
  initialData,
  className,
  labelClassName,
  isDisabled,
  autoFocusOutcome,
  onChange,
}: IMdfActivityOutcomeFieldsProps) => {
  const { actualsValueType, actualsValueTypes } = useActualsData()
  const manuallyEditedEstimateFormKeys = useRef(new Set<string>())

  const [formFunnelSizeUsd] = useGetFormDataField<Nullable<number> | undefined>(
    formKey,
    'funnelSizeUsd'
  )
  const [formConversionRate] = useGetFormDataField<Nullable<number> | undefined>(
    formKey,
    'conversionRate'
  )
  const [formEstimatedSalesUsd, setFormEstimatedSalesUsd] = useGetFormDataField<
    Nullable<number> | undefined
  >(formKey, 'estimatedSalesUsd')

  const funnelSizeUsd =
    formFunnelSizeUsd === undefined ? initialData.funnelSizeUsd : formFunnelSizeUsd
  const conversionRate =
    formConversionRate === undefined ? initialData.conversionRate : formConversionRate
  const estimatedSalesUsd =
    formEstimatedSalesUsd === undefined ? initialData.estimatedSalesUsd : formEstimatedSalesUsd
  const isEstimatedSalesAutoCalculated =
    initialData.estimatedSalesUsd === null && !manuallyEditedEstimateFormKeys.current.has(formKey)

  const outcomeValueTypes = useMemo(
    () =>
      actualsValueTypes?.length
        ? actualsValueTypes
        : Object.values(ActualsValueTypes).filter((type) => type !== ActualsValueTypes.None),
    [actualsValueTypes]
  )
  const resolvedOutcomeValueType = initialData.outcomeValueType || actualsValueType
  const { value: outcomeValueType, handleChange: handleOutcomeValueTypeChange } =
    useFormField<string>({
      formKey,
      field: 'outcomeValueType',
      defaultValue: resolvedOutcomeValueType,
      isDisabled,
      onChange: (value) => onChange?.('outcomeValueType', value),
    })

  const handleMetricChange = useCallback(
    (field: IMdfOutcomeMetricField['field'], value: Nullable<number>) => {
      if (field === 'estimatedSalesUsd') {
        manuallyEditedEstimateFormKeys.current.add(formKey)
      }

      onChange?.(field, value)
    },
    [formKey, onChange]
  )

  useEffect(() => {
    if (!isEstimatedSalesAutoCalculated) {
      return
    }

    const calculatedEstimatedSales = calculateMdfEstimatedSales(funnelSizeUsd, conversionRate)
    if (estimatedSalesUsd === calculatedEstimatedSales) {
      return
    }

    setFormEstimatedSalesUsd(calculatedEstimatedSales)
    onChange?.('estimatedSalesUsd', calculatedEstimatedSales)
  }, [
    conversionRate,
    estimatedSalesUsd,
    funnelSizeUsd,
    isEstimatedSalesAutoCalculated,
    onChange,
    setFormEstimatedSalesUsd,
  ])

  useEffect(() => {
    if (!initialData.outcomeValueType && outcomeValueType) {
      onChange?.('outcomeValueType', outcomeValueType)
    }
  }, [initialData.outcomeValueType, onChange, outcomeValueType])

  return (
    <div className={cn(styles.root, className)}>
      <div className={cn(styles.controls, styles.wide)}>
        {/* MDF Outcome amounts stay in USD; this single-option control is display-only. */}
        <RowButtons
          value={P360Currency.USD}
          options={[P360Currency.USD]}
          generateTranslationKey={(value) => value}
          onOptionClick={() => undefined}
          isDisabled={isDisabled}
          size="small"
        />
        <div className={styles.separator} />
        <PartnerTypeButtons
          buttonCurrType={outcomeValueType}
          setButtonCurrType={handleOutcomeValueTypeChange}
          buttonTypes={outcomeValueTypes}
          translationKey="jbp-partner-actuals-value-type"
          disabled={isDisabled}
          size="small"
        />
      </div>

      {MDF_OUTCOME_METRIC_FIELDS.map((config) => (
        <div key={config.field} className={styles.field}>
          <span className={cn(styles.label, labelClassName)}>
            <Translation text={config.labelKey} span />
          </span>
          <FormNumberInput
            formKey={formKey}
            field={config.field}
            defaultValue={initialData[config.field]}
            min={config.min}
            max={config.max}
            isDisabled={isDisabled}
            onChange={(value) => handleMetricChange(config.field, value)}
          />
        </div>
      ))}

      <div className={cn(styles.field, styles.wide)}>
        <MdfOutcomeItemsEditor
          formKey={formKey}
          initialValue={initialData.outcome}
          labelClassName={labelClassName}
          autoFocus={autoFocusOutcome}
          isDisabled={isDisabled}
          onChange={(value) => onChange?.('outcome', value)}
        />
      </div>
    </div>
  )
}

MdfActivityOutcomeFields.displayName = 'MdfActivityOutcomeFields'
